import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Trash2, LayoutTemplate } from "lucide-react";

function Recipients({ users, selected, toggle, selectAll }) {
  return (
    <div className="min-w-0 rounded-3xl border-2 border-ink bg-white shadow-pop-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold">Recipients</h3>
        <button className="text-xs underline font-semibold" onClick={selectAll}>Select all</button>
      </div>
      <div className="space-y-1 max-h-[26rem] overflow-auto">
        {users.map((u) => (
          <label key={u.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#FFFDF5] cursor-pointer" data-testid={`recipient-${u.id}`}>
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} className="h-4 w-4 accent-[#FF5722]" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{u.name}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email} · {ROLE_LABELS[u.role]}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function TemplatePicker({ templates, onApply }) {
  return (
    <Select value="" onValueChange={onApply}>
      <SelectTrigger className="h-9 border-2 border-ink rounded-xl text-xs w-40" data-testid="template-select">
        <LayoutTemplate size={13} className="mr-1" /><SelectValue placeholder="Templates" />
      </SelectTrigger>
      <SelectContent>
        {templates.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No templates yet</div>}
        {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function EmailTab() {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const loadTemplates = useCallback(() => { apiGet("/manage/email-templates").then(setTemplates).catch(() => {}); }, []);
  useEffect(() => { apiGet("/manage/users").then(setUsers).catch(() => {}); loadTemplates(); }, [loadTemplates]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.body); toast.success(`Loaded "${t.name}"`); }
  };
  const saveTemplate = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Write a subject and message first");
    const name = window.prompt("Template name?");
    if (!name) return;
    try { await apiPost("/manage/email-templates", { name, subject, body }); toast.success("Template saved"); loadTemplates(); }
    catch (e) { toast.error(e.message); }
  };
  const deleteTemplate = async (id) => { try { await apiDelete(`/manage/email-templates/${id}`); loadTemplates(); } catch (e) { toast.error(e.message); } };

  const send = async () => {
    if (selected.length === 0) return toast.error("Select at least one recipient");
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    setBusy(true);
    try {
      const res = await apiPost("/manage/email", { user_ids: selected, subject, body });
      if (res.provider_configured) toast.success(`Queued ${res.recipients} · delivered ${res.dispatched}`);
      else toast.success(`Recorded ${res.recipients} message(s).`, { duration: 9000 });
      setSubject(""); setBody(""); setSelected([]);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-5" data-testid="email-tab">
      <div className="min-w-0 rounded-3xl border-2 border-ink bg-white shadow-pop-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-extrabold text-lg flex items-center gap-2"><Mail size={18} /> Compose</h3>
          <TemplatePicker templates={templates} onApply={applyTemplate} />
        </div>
        <Label>Subject</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="border-2 border-ink h-11 rounded-xl mt-1 mb-4" data-testid="email-subject" />
        <Label>Message</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="border-2 border-ink rounded-xl mt-1 mb-4 resize-none" data-testid="email-body" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={send} disabled={busy} className="rounded-full border-2 border-ink shadow-pop press font-bold" data-testid="send-email">
            {busy ? "Sending…" : `Send to ${selected.length} recipient(s)`}
          </Button>
          <Button onClick={saveTemplate} variant="outline" className="rounded-full border-2 border-ink press font-bold" data-testid="save-template">Save as template</Button>
        </div>

        {templates.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {templates.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full border-2 border-ink bg-white" data-testid={`template-chip-${t.id}`}>
                {t.name}
                <button onClick={() => deleteTemplate(t.id)} className="opacity-60 hover:opacity-100"><Trash2 size={11} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <Recipients users={users} selected={selected} toggle={toggle} selectAll={() => setSelected(users.map((u) => u.id))} />
    </div>
  );
}
